/* Pixel-based artwork checks and local editing. No network calls or upscaling. */
(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.FFCArtworkQualityCore = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var MAX_PIXELS = 20000000;
  var MAX_EDIT_PIXELS = 10000000;
  var ALPHA_EDGE_RADIUS = 4;
  var PRESETS = Object.freeze({
    chest: Object.freeze({ widthMm: 110, heightMm: 110 }),
    back: Object.freeze({ widthMm: 210, heightMm: 297 }),
    sleeve: Object.freeze({ widthMm: 75, heightMm: 75 }),
    neck: Object.freeze({ widthMm: 80, heightMm: 80 })
  });

  function finite(value) { return typeof value === 'number' && Number.isFinite(value); }
  function dimension(value) { return finite(value) && value > 0 && Number.isInteger(value); }
  function clamp(value, min, max) { return Math.min(max, Math.max(min, value)); }
  function option(value, fallback, min, max) {
    return finite(value) ? clamp(value, min, max) : fallback;
  }
  function fail(code) {
    var error = new RangeError(code);
    error.code = code;
    throw error;
  }
  function checkDimensions(width, height) {
    if (!dimension(width) || !dimension(height)) fail('invalid_image');
    if (width > MAX_PIXELS / height) fail('too_large');
    return width * height;
  }
  function checkImage(image) {
    if (!image) fail('invalid_image');
    var count = checkDimensions(image.width, image.height);
    if (!image.data || image.data.length !== count * 4) fail('invalid_image');
    return count;
  }
  function cloneImage(image) {
    checkImage(image);
    return { width: image.width, height: image.height, data: new Uint8ClampedArray(image.data) };
  }

  // Visit pixels whose clipped radius-4 square contains BOTH real transparent
  // and opaque pixels. Two separable sliding windows are exactly a Chebyshev
  // neighbourhood, not an approximation based on the amount of transparency.
  // One byte per pixel; no pixel objects, recursion or invented outside pixels.
  function visitAlphaEdgeNeighbourhood(data, width, height, visit) {
    var horizontal = new Uint8Array(width * height);
    var radius = ALPHA_EDGE_RADIUS;
    var x, y, p, a, mask, transparent, opaque, outgoing, incoming;
    for (y = 0; y < height; y += 1) {
      transparent = 0; opaque = 0;
      var row = y * width;
      for (x = 0; x <= Math.min(radius, width - 1); x += 1) {
        a = data[(row + x) * 4 + 3] >> 1;
        if (a === 0) transparent += 1;
        else if (a === 127) opaque += 1;
      }
      for (x = 0; x < width; x += 1) {
        horizontal[row + x] = (transparent ? 1 : 0) | (opaque ? 2 : 0);
        outgoing = x - radius;
        if (outgoing >= 0) {
          a = data[(row + outgoing) * 4 + 3] >> 1;
          if (a === 0) transparent -= 1;
          else if (a === 127) opaque -= 1;
        }
        incoming = x + radius + 1;
        if (incoming < width) {
          a = data[(row + incoming) * 4 + 3] >> 1;
          if (a === 0) transparent += 1;
          else if (a === 127) opaque += 1;
        }
      }
    }
    for (x = 0; x < width; x += 1) {
      transparent = 0; opaque = 0;
      for (y = 0; y <= Math.min(radius, height - 1); y += 1) {
        mask = horizontal[y * width + x];
        if (mask & 1) transparent += 1;
        if (mask & 2) opaque += 1;
      }
      for (y = 0, p = x; y < height; y += 1, p += width) {
        if (transparent && opaque) visit(p);
        outgoing = y - radius;
        if (outgoing >= 0) {
          mask = horizontal[outgoing * width + x];
          if (mask & 1) transparent -= 1;
          if (mask & 2) opaque -= 1;
        }
        incoming = y + radius + 1;
        if (incoming < height) {
          mask = horizontal[incoming * width + x];
          if (mask & 1) transparent += 1;
          if (mask & 2) opaque += 1;
        }
      }
    }
  }

  // Match the gateway's GD 7-bit alpha precision when measuring pixels. Alpha
  // 0/1 is transparent; 254/255 is opaque. Editing retains the original 8-bit
  // buffer unchanged. Hidden RGB never creates printable-content bounds.
  function scanRGBA(data, width, height) {
    var count = checkImage({ data: data, width: width, height: height });
    var minX = width, minY = height, maxX = -1, maxY = -1;
    var visible = 0, partial = 0, transparent = 0;
    for (var y = 0, index = 3; y < height; y += 1) {
      for (var x = 0; x < width; x += 1, index += 4) {
        var alpha = data[index] >> 1;
        if (alpha === 0) { transparent += 1; continue; }
        visible += 1;
        if (alpha < 127) partial += 1;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
    var edge = 0;
    if (partial && transparent && visible > partial) {
      visitAlphaEdgeNeighbourhood(data, width, height, function (pixel) {
        var alpha = data[pixel * 4 + 3] >> 1;
        if (alpha > 0 && alpha < 127) edge += 1;
      });
    }
    return {
      width: width,
      height: height,
      contentBounds: visible ? { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 }
        : { x: 0, y: 0, width: 0, height: 0 },
      hasTransparency: transparent + partial > 0,
      partialAlphaRatio: visible ? partial / visible : 0,
      empty: visible === 0,
      visiblePixels: visible,
      transparentPixels: transparent,
      partialAlphaPixels: partial,
      edgeAlphaPixels: edge,
      interiorPartialAlphaPixels: partial - edge,
      interiorPartialAlphaRatio: visible ? (partial - edge) / visible : 0,
      totalPixels: count
    };
  }

  function analyse(meta, preset) {
    meta = meta || {};
    var area = PRESETS[preset || 'chest'];
    var result = { status: 'red', ppi: 0, widthMm: 0, heightMm: 0, requiredWidth: 0, requiredHeight: 0, reasons: [] };
    function red(reason) { result.reasons.push(reason); return result; }
    if (!area) return red('manual_review');
    var format = String(meta.format || '').toLowerCase().trim().replace(/^\./, '').split(';')[0];
    if (!/^(png|jpe?g|webp|image\/(png|jpe?g|webp))$/.test(format)) return red('manual_review');
    if (meta.empty || !dimension(meta.width) || !dimension(meta.height)) return red('empty');
    if (meta.width > MAX_PIXELS / meta.height) return red('too_large');
    var bounds = meta.contentBounds || { x: 0, y: 0, width: meta.width, height: meta.height };
    if (!dimension(bounds.width) || !dimension(bounds.height)) return red('empty');
    if (!finite(bounds.x) || !finite(bounds.y) || bounds.x < 0 || bounds.y < 0 ||
        bounds.x + bounds.width > meta.width || bounds.y + bounds.height > meta.height) return red('manual_review');

    var scaleMm = Math.min(area.widthMm / bounds.width, area.heightMm / bounds.height);
    result.widthMm = bounds.width * scaleMm;
    result.heightMm = bounds.height * scaleMm;
    result.requiredWidth = Math.round(result.widthMm / 25.4 * 300);
    result.requiredHeight = Math.round(result.heightMm / 25.4 * 300);

    // Source dimensions describe the native-pixel canvas in the current crop's
    // coordinate system. Callers must carry that density through each crop.
    // A larger resized canvas can never manufacture native detail.
    var sourceWidth = meta.sourceWidth === undefined ? meta.width : meta.sourceWidth;
    var sourceHeight = meta.sourceHeight === undefined ? meta.height : meta.sourceHeight;
    if (!finite(sourceWidth) || !finite(sourceHeight) || sourceWidth <= 0 || sourceHeight <= 0) return red('manual_review');
    var nativeWidth = bounds.width * Math.min(1, sourceWidth / meta.width);
    var nativeHeight = bounds.height * Math.min(1, sourceHeight / meta.height);
    result.ppi = Math.min(nativeWidth / result.widthMm, nativeHeight / result.heightMm) * 25.4;
    // 0.05 PPI covers conventional whole-pixel rounding at 300 PPI (e.g. A4).
    if (result.ppi < 150) result.reasons.push('low_resolution');
    else if (result.ppi < 299.95) result.reasons.push('borderline_resolution');
    if (meta.hasTransparency !== true) result.reasons.push('opaque_background');
    // Only verified thin edges are exempt. Old/incomplete metadata keeps the
    // conservative raw-partial rule; absence of evidence never means no risk.
    var partialRatio = finite(meta.partialAlphaRatio) ? Math.max(0, meta.partialAlphaRatio) : 0;
    var counts = [meta.visiblePixels, meta.partialAlphaPixels, meta.edgeAlphaPixels, meta.interiorPartialAlphaPixels];
    var coherent = counts.every(function (n) { return finite(n) && Number.isInteger(n) && n >= 0; }) &&
      meta.visiblePixels > 0 && meta.partialAlphaPixels <= meta.visiblePixels &&
      meta.edgeAlphaPixels + meta.interiorPartialAlphaPixels === meta.partialAlphaPixels;
    if (coherent && Math.abs(partialRatio - meta.partialAlphaPixels / meta.visiblePixels) < 1e-10) {
      partialRatio = meta.interiorPartialAlphaPixels / meta.visiblePixels;
    }
    if (partialRatio > 0.01) result.reasons.push('semi_transparency');
    if (meta.processed) result.reasons.push('processed');
    result.status = result.ppi < 150 ? 'red' : (result.reasons.length ? 'yellow' : 'green');
    return result;
  }

  // Distance is RMS RGB difference, from 0 to 255; feather extends the colour
  // tolerance and softens only that colour fringe. This is not subject detection.
  function colourDistance(data, offset, colour) {
    var r = data[offset] - colour[0];
    var g = data[offset + 1] - colour[1];
    var b = data[offset + 2] - colour[2];
    return Math.sqrt((r * r + g * g + b * b) / 3);
  }
  function eachBorder(width, height, visit) {
    for (var x = 0; x < width; x += 1) {
      visit(x);
      if (height > 1) visit((height - 1) * width + x);
    }
    for (var y = 1; y < height - 1; y += 1) {
      visit(y * width);
      if (width > 1) visit(y * width + width - 1);
    }
  }
  function borderColour(image) {
    // A small fixed histogram avoids an unbounded map for photographic edges.
    var counts = new Uint32Array(4096);
    var sums = new Float64Array(4096 * 3);
    var best = -1;
    eachBorder(image.width, image.height, function (pixel) {
      var i = pixel * 4;
      if (image.data[i + 3] === 0) return;
      var r = image.data[i], g = image.data[i + 1], b = image.data[i + 2];
      var bin = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
      counts[bin] += 1;
      sums[bin * 3] += r; sums[bin * 3 + 1] += g; sums[bin * 3 + 2] += b;
      if (best < 0 || counts[bin] > counts[best]) best = bin;
    });
    return best < 0 ? null : [sums[best * 3] / counts[best], sums[best * 3 + 1] / counts[best], sums[best * 3 + 2] / counts[best]];
  }

  // No seed: estimate the dominant border colour and remove matching regions
  // connected to the image edge. x/y: sample and remove one selected region.
  // contiguous=false explicitly removes all matching pixels, including holes.
  // Every operation returns a fresh buffer so the caller can keep undo history.
  function removeBackground(image, options) {
    var count = checkImage(image);
    options = options || {};
    var output = cloneImage(image);
    var input = image.data, data = output.data, width = image.width;
    var tolerance = option(options.tolerance, 24, 0, 255);
    var feather = option(options.feather, 0, 0, 255);
    var limit = Math.min(255, tolerance + feather);
    var hasSeed = options.x !== undefined || options.y !== undefined;
    var seed = -1, colour;
    if (hasSeed) {
      if (!finite(options.x) || !finite(options.y) || options.x < 0 || options.y < 0 ||
          options.x >= width || options.y >= image.height) fail('invalid_seed');
      seed = Math.floor(options.y) * width + Math.floor(options.x);
      if (input[seed * 4 + 3] === 0) return output;
      colour = [input[seed * 4], input[seed * 4 + 1], input[seed * 4 + 2]];
    } else colour = borderColour(image);
    if (!colour) return output;

    function matches(pixel) {
      var offset = pixel * 4;
      return input[offset + 3] === 0 || colourDistance(input, offset, colour) <= limit;
    }
    function remove(pixel) {
      var offset = pixel * 4;
      if (input[offset + 3] === 0) return;
      var distance = colourDistance(input, offset, colour);
      if (distance <= tolerance) data[offset + 3] = 0;
      else if (feather && distance < tolerance + feather) {
        data[offset + 3] = Math.round(input[offset + 3] * (distance - tolerance) / feather);
      }
    }
    if (options.contiguous === false) {
      for (var p = 0; p < count; p += 1) if (matches(p)) remove(p);
      return output;
    }

    // Four-neighbour flood. Each pixel is visited/enqueued at most once; rows
    // never wrap at the image edge, and the typed queue is bounded by 20 MP.
    var visited = new Uint8Array(count);
    var queue = new Uint32Array(count);
    var head = 0, tail = 0;
    function enqueue(pixel) {
      if (visited[pixel]) return;
      visited[pixel] = 1;
      if (matches(pixel)) queue[tail++] = pixel;
    }
    if (hasSeed) enqueue(seed);
    else eachBorder(width, image.height, enqueue);
    while (head < tail) {
      var current = queue[head++];
      remove(current);
      var column = current % width;
      if (column > 0) enqueue(current - 1);
      if (column + 1 < width) enqueue(current + 1);
      if (current >= width) enqueue(current - width);
      if (current + width < count) enqueue(current + width);
    }
    return output;
  }

  // Restore uses the untouched original in the same dimensions/coordinates.
  // A hard brush restores exact bytes; a soft brush blends the original alpha.
  function applyBrush(image, original, options) {
    checkImage(image);
    options = options || {};
    if (options.mode !== 'erase' && options.mode !== 'restore') fail('invalid_brush');
    if (!finite(options.x) || !finite(options.y) || !finite(options.radius) || options.radius <= 0) fail('invalid_brush');
    if (options.mode === 'restore') {
      checkImage(original);
      if (original.width !== image.width || original.height !== image.height) fail('invalid_original');
    }
    var output = cloneImage(image), data = output.data;
    var radius = options.radius;
    var hardness = option(options.hardness, 1, 0, 1);
    var inner = radius * hardness;
    var x0 = Math.max(0, Math.ceil(options.x - radius));
    var x1 = Math.min(image.width - 1, Math.floor(options.x + radius));
    var y0 = Math.max(0, Math.ceil(options.y - radius));
    var y1 = Math.min(image.height - 1, Math.floor(options.y + radius));
    for (var y = y0; y <= y1; y += 1) {
      for (var x = x0; x <= x1; x += 1) {
        var distance = Math.hypot(x - options.x, y - options.y);
        if (distance > radius) continue;
        var coverage = distance <= inner ? 1 : (radius - distance) / (radius - inner);
        if (coverage <= 0) continue;
        var offset = (y * image.width + x) * 4;
        if (options.mode === 'restore') {
          data[offset] = original.data[offset];
          data[offset + 1] = original.data[offset + 1];
          data[offset + 2] = original.data[offset + 2];
          data[offset + 3] = Math.round(data[offset + 3] + (original.data[offset + 3] - data[offset + 3]) * coverage);
        } else data[offset + 3] = Math.round(data[offset + 3] * (1 - coverage));
      }
    }
    return output;
  }

  // One optional mild pass, without resampling or inferred image content.
  // denoise/sharpen default on; refineAlpha is explicitly opt-in. RGB changes
  // are limited to +/-3 levels and opaque, low-contrast neighbourhoods. Flat
  // colours, near-black/white, alpha edges and narrow contrast features remain.
  function gentlyEnhance(image, options) {
    var count = checkImage(image);
    if (count > MAX_EDIT_PIXELS) fail('edit_limit');
    options = options || {};
    var denoise = options.denoise !== false;
    var sharpen = options.sharpen !== false;
    var refineAlpha = options.refineAlpha === true;
    var output = cloneImage(image), source = image.data, data = output.data;
    var width = image.width, height = image.height;
    if (denoise || sharpen) {
      for (var y = 1; y < height - 1; y += 1) {
        for (var x = 1; x < width - 1; x += 1) {
          var index = (y * width + x) * 4;
          if (source[index + 3] < 254) continue;
          var r = source[index], g = source[index + 1], b = source[index + 2];
          if ((r <= 8 && g <= 8 && b <= 8) || (r >= 247 && g >= 247 && b >= 247)) continue;
          var sumR = 0, sumG = 0, sumB = 0;
          var minR = 255, minG = 255, minB = 255, minL = 1020;
          var maxR = 0, maxG = 0, maxB = 0, maxL = 0, close = 0, safe = true;
          for (var dy = -1; dy <= 1 && safe; dy += 1) {
            for (var dx = -1; dx <= 1; dx += 1) {
              if (!dx && !dy) continue;
              var n = ((y + dy) * width + x + dx) * 4;
              var nr = source[n], ng = source[n + 1], nb = source[n + 2];
              var difference = Math.max(Math.abs(nr - r), Math.abs(ng - g), Math.abs(nb - b));
              if (source[n + 3] < 254 || difference > 24) { safe = false; break; }
              if (difference <= 3) close += 1;
              sumR += nr; sumG += ng; sumB += nb;
              minR = Math.min(minR, nr); minG = Math.min(minG, ng); minB = Math.min(minB, nb);
              maxR = Math.max(maxR, nr); maxG = Math.max(maxG, ng); maxB = Math.max(maxB, nb);
              var luminance = nr + 2 * ng + nb;
              minL = Math.min(minL, luminance); maxL = Math.max(maxL, luminance);
            }
          }
          if (!safe) continue;
          var dr = r - sumR / 8, dg = g - sumG / 8, db = b - sumB / 8;
          var residual = Math.max(Math.abs(dr), Math.abs(dg), Math.abs(db));
          var spread = Math.max(maxR - minR, maxG - minG, maxB - minB);
          var factor = 0;
          if (denoise && spread <= 10 && close <= 1 && residual >= 2 && residual <= 16) factor = -0.35;
          else if (sharpen && spread >= 12 && residual >= 1 && residual <= 12 &&
            r + 2 * g + b > minL + 1 && r + 2 * g + b < maxL - 1) factor = 0.2;
          if (!factor) continue;
          data[index] = clamp(Math.round(r + clamp(dr * factor, -3, 3)), 0, 255);
          data[index + 1] = clamp(Math.round(g + clamp(dg * factor, -3, 3)), 0, 255);
          data[index + 2] = clamp(Math.round(b + clamp(db * factor, -3, 3)), 0, 255);
        }
      }
    }
    if (refineAlpha) {
      // At most four alpha levels, only at an existing real transparency edge.
      // Interior translucency, faint isolated strokes and all middle alpha
      // values are preserved. The default path changes no alpha values at all.
      visitAlphaEdgeNeighbourhood(source, width, height, function (pixel) {
        var alpha = source[pixel * 4 + 3];
        if (alpha > 0 && alpha <= 4) data[pixel * 4 + 3] = 0;
        else if (alpha >= 251 && alpha < 255) data[pixel * 4 + 3] = 255;
      });
    }
    return output;
  }

  function cropToContent(image, padding) {
    var scan = scanRGBA(image.data, image.width, image.height);
    if (scan.empty) return { width: 1, height: 1, data: new Uint8ClampedArray(4), bounds: scan.contentBounds, empty: true };
    padding = Math.floor(option(padding, 0, 0, Math.max(image.width, image.height)));
    var b = scan.contentBounds;
    var x = Math.max(0, b.x - padding), y = Math.max(0, b.y - padding);
    var width = Math.min(image.width, b.x + b.width + padding) - x;
    var height = Math.min(image.height, b.y + b.height + padding) - y;
    var data = new Uint8ClampedArray(width * height * 4);
    for (var row = 0; row < height; row += 1) {
      var start = ((y + row) * image.width + x) * 4;
      data.set(image.data.subarray(start, start + width * 4), row * width * 4);
    }
    return { width: width, height: height, data: data, bounds: { x: x, y: y, width: width, height: height }, empty: false };
  }

  return { MAX_PIXELS: MAX_PIXELS, MAX_EDIT_PIXELS: MAX_EDIT_PIXELS, PRESETS: PRESETS, analyse: analyse, scanRGBA: scanRGBA,
    removeBackground: removeBackground, applyBrush: applyBrush, gentlyEnhance: gentlyEnhance, cropToContent: cropToContent, cloneImage: cloneImage };
});
